import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { spacing, fontStyles, colors, buildPageSpacing } from '../../styles';
import * as stores from '../../stores';
import Page from '../Page';
import Header from '../Header';

interface AddPortfolioProps {
  isOnboarding?: boolean;
}

const AddPortfolio: React.FC<AddPortfolioProps> = ({ isOnboarding = false }) => {
  const { t } = useTranslation();
  const [portfolioName, setPortfolioName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const history = useHistory();

  const handleSubmit = async () => {
    if (!portfolioName.trim()) return;

    setIsCreating(true);

    try {
      // Store the portfolio name temporarily and navigate to AddTickers
      sessionStorage.setItem('newPortfolioName', portfolioName.trim());
      history.push('/portfolios/add-tickers/new');
    } catch (error) {
      console.error('Error navigating to add tickers:', error);
      setIsCreating(false);
    }
  };

  const pageStyles = css`
    padding: ${spacing.milli};
    padding-bottom: calc(80px + ${spacing.regular});

    @media (max-width: 359px) {
      padding: ${spacing.micro} ${spacing.micro};
      padding-bottom: calc(80px + ${spacing.regular});
    }
  `;

  const headingStyles = css`
    ${fontStyles.shark};
    margin: 0;
    margin-top: ${spacing.centi};
    margin-bottom: ${spacing.deci};
  `;

  const instructionStyles = css`
    ${fontStyles.dog};
    color: ${colors.silver};
  `;

  const inputStyles = css`
    width: 100%;
    border: 1px solid ${colors.cloud};
    border-radius: 8px;
    padding: ${spacing.deci};
    ${fontStyles.cat};
    margin-top: ${spacing.deci};
    background-color: ${colors.sugar};
    color: ${colors.liquorice};

    html[data-theme='dark'] & {
      background-color: #242424;
      color: ${colors.sugar};
      border-color: ${colors.tungsten};
    }

    &:focus {
      outline: none;
      border-color: ${colors.petrolBlue};
    }

    &::placeholder {
      ${fontStyles.cat};
      color: ${colors.silver};
    }
  `;

  const buttonStyles = css`
    display: block;
    width: 100%;
    background-color: ${colors.petrolBlue};
    color: ${colors.snow};
    border: none;
    border-radius: 8px;
    padding: ${spacing.deci};
    ${fontStyles.horse};
    cursor: pointer;
    opacity: ${!portfolioName.trim() ? 0.5 : 1};
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      opacity: ${!portfolioName.trim() ? 0.5 : 0.9};
    }

    @media (max-width: 359px) {
      padding: ${spacing.centi} ${spacing.deci};
      font-size: 14px;
    }
  `;

  // Handle skipping portfolio creation in onboarding
  const handleSkip = () => {
    if (isOnboarding) {
      // Go to payment page to continue onboarding
      stores.showPaymentModalByPreference('onboarding_portfolio_skip');
    }
  };

  return (
    <Page showNavigationBar={false}>
      <Header
        headingText=""
        onBack={() => {
          // Navigate back based on whether we are in the onboarding flow
          isOnboarding ? history.push('/onboarding/0') : history.push('/portfolios');
        }}
        rightAction={
          isOnboarding ? (
            <button
              css={css`
                background: none;
                border: none;
                color: ${colors.petrolBlue};
                ${fontStyles.lion};
                padding: ${spacing.centi};
                cursor: pointer;
                &:hover {
                  text-decoration: underline;
                }
                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
              onClick={handleSkip}
            >
              {t('portfolios.add.skip')}
            </button>
          ) : null
        }
      />
      <div css={pageStyles}>
        <h1 css={headingStyles}>{isOnboarding ? t('portfolios.add.createTitle') : t('portfolios.add.addTitle')}</h1>
        <p css={instructionStyles}>{t('portfolios.add.nameInstruction')}</p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          css={css`
            margin: 0;
          `}
        >
          <input
            type="text"
            css={css`
              ${inputStyles};
              margin-top: ${spacing.regular};
            `}
            value={portfolioName}
            onChange={(e) => setPortfolioName(e.target.value)}
            enterKeyHint="go"
            autoCapitalize="words"
            autoComplete="off"
            placeholder={t('portfolios.add.placeholder')}
          />
        </form>
      </div>

      {/* Fixed continue button at bottom */}
      <div
        css={css`
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          padding: ${spacing.regular};
          padding-bottom: ${buildPageSpacing(spacing.regular, 'BOTTOM')};
          background-color: ${colors.sugar};
          border-top: 1px solid ${colors.cloud};
          z-index: 1000;
          transition: bottom 0.3s ease;

          html[data-theme='dark'] & {
            background-color: #1a1a1a;
            border-color: ${colors.tungsten};
          }
        `}
      >
        <button css={buttonStyles} onClick={handleSubmit} disabled={!portfolioName.trim() || isCreating}>
          {isCreating
            ? t('portfolios.add.creating')
            : isOnboarding
              ? t('portfolios.add.createPortfolio')
              : t('portfolios.add.continue')}
        </button>
      </div>
    </Page>
  );
};

export default AddPortfolio;
